import React, { useCallback, useMemo, useState } from 'react';

import type { FiltrosOficina } from '../../../api/dashboardOficinaApi';
import AssistenteChat from '../components/AssistenteChat';
import EventosTab from '../components/EventosTab';
import OficinaFiltroModal from '../components/OficinaFiltroModal';
import FrotaPerfilTab from '../components/FrotaPerfilTab';
import PrazosTab from '../components/PrazosTab';
import RegiaoTab from '../components/RegiaoTab';
import SlaTab from '../components/SlaTab';
import VisaoGeralOficinaTab from '../components/VisaoGeralOficinaTab';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
// Casco, cabeçalho e barra de abas são as classes compartilhadas do sistema. Import explícito:
// contar com o CSS carregado por outra tela é acoplamento invisível (BUG-V74).
import '../../recepcao/pages/Recepcao.css';
import '../../../shared/ui/dashboard-base.css';

/**
 * Dashboard Oficina (at-86) — a operação da oficina, só para ADMIN.
 *
 * ⚠️ A população é o ATENDIMENTO: veículo que está ou já esteve na oficina. Não confundir com o
 * "Contac — Dashboard da Associação", que descreve a carteira inteira (arrecadação, frota
 * protegida). A separação é decisão do Victor, 27/08/2026 — e é por ela que esta tela lê o banco
 * da Oficina e não depende do Contac estar sincronizado.
 *
 * ⚠️ AS ABAS SÃO UM REGISTRO, com `disponivel`: as previstas ficam declaradas e OCULTAS até
 * existir dado. Aba que abre num "em preparação" promete número inexistente e tem a mesma
 * silhueta de uma aba que falhou ao carregar.
 *
 * ⚠️ OS FILTROS VIVEM AQUI e são preservados na troca de aba — as duas abas precisam falar do
 * mesmo recorte, senão o leitor compara períodos diferentes sem perceber.
 */

type AbaId = 'VISAO_GERAL' | 'PRAZOS' | 'SLA' | 'FROTA_PERFIL' | 'REGIAO' | 'EVENTOS';

interface AbaDefinicao {
  id: AbaId;
  rotulo: string;
  /** `false` = prevista, sem dado ainda. Não vira botão nem conteúdo. */
  disponivel: boolean;
}

const ABAS: AbaDefinicao[] = [
  { id: 'VISAO_GERAL', rotulo: 'Visão Geral', disponivel: true },
  /*
   * ⚠️ Prazos e Tempos & Equipe NÃO são a mesma coisa dividida em duas, e a ordem importa:
   * Prazos é o presente (o que está atrasado agora) e vem antes porque é a leitura de operação;
   * Tempos & Equipe é retrospectiva. O nome "SLA & Tempos" saiu na at-88 justamente porque
   * prometia as duas e entregava só a segunda.
   */
  { id: 'PRAZOS', rotulo: 'Prazos', disponivel: true },
  { id: 'SLA', rotulo: 'Tempos & Equipe', disponivel: true },
  { id: 'FROTA_PERFIL', rotulo: 'Frota & Perfil', disponivel: true },
  { id: 'REGIAO', rotulo: 'Região', disponivel: true },
  // at-133: a coleta de eventos do SGA (sync `contac/at-09`) passou a ter carga.
  { id: 'EVENTOS', rotulo: 'Eventos', disponivel: true },
];

const ABAS_DISPONIVEIS = ABAS.filter((aba) => aba.disponivel);

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que é este painel',
    conteudo: (
      <>
        <p>
          A leitura gerencial da <strong>oficina</strong>: só entram veículos que estão ou já
          estiveram aqui. Nada nesta tela altera veículo, esteira, hora ou checklist — é só
          consulta.
        </p>
        <p>
          Não confunda com o <strong>Dashboard da Associação</strong>, no Contac: aquele descreve a
          carteira inteira da empresa (arrecadação, veículos protegidos, associados), inclusive
          veículos que nunca passaram pela oficina.
        </p>
      </>
    ),
  },
  {
    titulo: 'O filtro de situação muda tudo',
    conteudo: (
      <>
        <p>
          Em <strong>Filtros</strong> você escolhe entre <strong>Na oficina</strong> (atendimentos
          em andamento), <strong>Entregues</strong> (já encerrados) e <strong>Os dois</strong>.
          As abas obedecem essa escolha, com uma exceção explicada abaixo.
        </p>
        <p>
          A diferença é grande: tempos médios calculados só sobre quem está na oficina descrevem o
          que ainda não terminou; só sobre entregues, o que já acabou. Ao comparar dois números,
          confira antes se o recorte é o mesmo — ele aparece escrito ao lado do botão Filtros.
        </p>
      </>
    ),
  },
  {
    titulo: 'Prazos e Tempos & Equipe respondem coisas diferentes',
    conteudo: (
      <>
        <p>
          <strong>Prazos</strong> é o <strong>agora</strong>: quais veículos estão parados além do
          prazo da etapa neste momento, quais estão perto disso, quais estão numa etapa sem prazo
          cadastrado e quais não recebem trabalho há dias. É a aba de quem precisa agir hoje.
        </p>
        <p>
          <strong>Tempos & Equipe</strong> é o <strong>retrospecto</strong>: quanto tempo se gastou,
          quem trabalhou quanto, e se os prazos foram cumpridos nas etapas <strong>que já
          terminaram</strong>. Um veículo atrasado neste momento não aparece nela — ele só entra
          quando sai da esteira.
        </p>
        <p>
          Por isso a aba Prazos <strong>ignora o período e a situação</strong> escolhidos em
          Filtros, e avisa quando você os aplica: o veículo mais atrasado costuma ser o que entrou
          antes do período que você escolheu, e filtrar por data o esconderia.
        </p>
        <p>
          A régua de <strong>em risco</strong> (80% do prazo da etapa) e <strong>atrasado</strong> é a
          mesma que a tela do setor de Eventos mostra ao lado da previsão de cada veículo: as duas
          telas dizem a mesma coisa sobre o mesmo veículo.
        </p>
      </>
    ),
  },
  {
    titulo: 'De onde vêm os números',
    conteudo: (
      <p>
        Do banco da própria oficina: cadastro de veículos, histórico de esteiras e as atividades
        registradas pelos colaboradores. É o mesmo dado das telas de operação — o que muda aqui é
        que ele aparece somado, e não veículo a veículo.
      </p>
    ),
  },
  {
    titulo: 'Eventos: por que o veículo entrou',
    conteudo: (
      <p>
        A aba <strong>Eventos</strong> cruza cada atendimento com o evento do SGA que o originou:
        o motivo, quem deu causa e quanto tempo cada motivo fica na oficina. Ela tem um{' '}
        <strong>?</strong> próprio explicando o que a cópia do SGA cobre e o que não cobre.
      </p>
    ),
  },
  {
    titulo: 'Retrabalho',
    conteudo: (
      <p>
        Na <strong>Visão Geral</strong>, o card <strong>Retrabalho</strong> mostra quantos dos
        veículos entregues voltaram em garantia. Ele olha só os entregues, independente do filtro de
        situação. O <strong>?</strong> da Visão Geral explica como ler o número por esteira.
      </p>
    ),
  },
  {
    titulo: 'Perguntar ao assistente',
    conteudo: (
      <>
        <p>
          O botão <strong>Perguntar ao assistente</strong> abre um chat que responde em texto sobre
          os <strong>mesmos números</strong> das abas. Ele não tem fonte de dados própria: para cada
          pergunta, consulta as mesmas apurações que a tela usa e escreve a resposta sobre o
          resultado. Cada resposta traz, no rodapé, quais consultas foram feitas.
        </p>
        <p>
          As respostas seguem o <strong>recorte aplicado na tela</strong> — situação e período —,
          salvo quando você pedir outro na própria pergunta. A resposta sempre diz qual recorte usou;
          confira antes de comparar um número do chat com um número de outra aba.
        </p>
        <p>
          O assistente é <strong>somente leitura</strong>: não altera veículo, esteira, hora nem
          checklist. Ele também não enxerga nada fora deste painel — arrecadação e mensalidade são
          do Contac, e a situação individual de um veículo está nas telas de operação. Perguntado
          sobre isso, ele diz que não cobre, em vez de estimar.
        </p>
        <p>
          Se o chat abrir desabilitado, o assistente não está habilitado neste ambiente: é
          configuração de servidor, e a TI resolve.
        </p>
      </>
    ),
  },
  {
    titulo: 'Quem enxerga este painel',
    conteudo: (
      <p>
        Apenas o perfil <strong>Administrador</strong>. A tela cruza produtividade por pessoa —
        quanto cada colaborador trabalhou, em que esteira, com que percentual de pausa —, e por isso
        não faz parte da superfície dos demais perfis, inclusive o de TI.
      </p>
    ),
  },
];

interface Props {
  sidebarRef?: React.RefObject<HTMLElement>;
}

const ROTULO_SITUACAO: Record<string, string> = {
  'na-oficina': 'Na oficina',
  entregues: 'Entregues',
  ambos: 'Na oficina e entregues',
};

const DashboardOficinaPage: React.FC<Props> = ({ sidebarRef }) => {
  const [abaAtiva, setAbaAtiva] = useState<AbaId>('VISAO_GERAL');
  const [filtros, setFiltros] = useState<FiltrosOficina>({});
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [assistenteAberto, setAssistenteAberto] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [esteiras, setEsteiras] = useState<Array<{ id: string; nome: string }>>([]);
  const [janela, setJanela] = useState<{ dataInicio: string; dataFim: string } | null>(null);

  const registrarJanela = useCallback((nova: { dataInicio: string; dataFim: string }) => {
    setJanela((atual) =>
      atual && atual.dataInicio === nova.dataInicio && atual.dataFim === nova.dataFim ? atual : nova,
    );
  }, []);

  const registrarEsteiras = useCallback((lista: Array<{ id: string; nome: string }>) => {
    setEsteiras((atual) => (atual.length === 0 && lista.length > 0 ? lista : atual));
  }, []);

  const periodoEscrito = useMemo(() => {
    if (!janela) return null;
    const br = (iso: string) => iso.split('-').reverse().join('/');
    return `${br(janela.dataInicio)} a ${br(janela.dataFim)}`;
  }, [janela]);

  const abasFuturas = ABAS.filter((aba) => !aba.disponivel);
  const situacaoEscrita = ROTULO_SITUACAO[filtros.situacao ?? 'ambos'];

  return (
    <div className="ConteDo recepcao-conte-do do-pagina">
      <div className="recepcao-header-row">
        <div
          className="recepcao-title"
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          Dashboard Oficina
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      <div className="do-controles">
        <button type="button" className="do-btn-filtros" onClick={() => setFiltrosAbertos(true)}>
          Filtros
        </button>
        {/*
          at-87 — o assistente lê o MESMO recorte aplicado aqui, por isso o botão fica ao lado do
          de Filtros: os dois governam o mesmo conjunto de veículos.
        */}
        <button type="button" className="do-btn-filtros" onClick={() => setAssistenteAberto(true)}>
          Perguntar ao assistente
        </button>
        <span className="do-periodo">
          {situacaoEscrita}
          {/*
            ⚠️ O período só é escrito quando o usuário ESCOLHEU um. Sem filtro, o padrão é o
            histórico completo (decisão de 27/08/2026) — anunciar uma data de início daria a
            impressão de um recorte que não existe.
          */}
          {filtros.dataInicio || filtros.dataFim
            ? ` · Entradas de ${periodoEscrito}`
            : ' · Todo o período'}
        </span>
      </div>

      <div className="recepcao-tabs">
        {ABAS_DISPONIVEIS.map((aba) => (
          <button
            key={aba.id}
            type="button"
            className={`recepcao-tab ${abaAtiva === aba.id ? 'active' : ''}`}
            onClick={() => setAbaAtiva(aba.id)}
          >
            {aba.rotulo}
          </button>
        ))}
      </div>

      {/*
        As abas são MONTADAS E DESMONTADAS na troca (e não escondidas com display:none), para a
        aba que entra sempre recarregar com o filtro em vigor.
      */}
      {abaAtiva === 'VISAO_GERAL' && (
        <VisaoGeralOficinaTab
          filtros={filtros}
          onJanela={registrarJanela}
          onEsteiras={registrarEsteiras}
          sidebarRef={sidebarRef}
        />
      )}
      {/*
        A aba Prazos não recebe `onJanela`: ela não tem janela — descreve o pátio agora, e o
        período da tela não se aplica a ela. Repassar a janela faria o rodapé da página descrever
        um recorte que esta aba não usou.
      */}
      {abaAtiva === 'PRAZOS' && <PrazosTab filtros={filtros} sidebarRef={sidebarRef} />}
      {abaAtiva === 'SLA' && <SlaTab filtros={filtros} onJanela={registrarJanela} sidebarRef={sidebarRef} />}
      {abaAtiva === 'FROTA_PERFIL' && (
        <FrotaPerfilTab filtros={filtros} onJanela={registrarJanela} sidebarRef={sidebarRef} />
      )}
      {abaAtiva === 'REGIAO' && (
        <RegiaoTab filtros={filtros} onJanela={registrarJanela} sidebarRef={sidebarRef} />
      )}
      {abaAtiva === 'EVENTOS' && (
        <EventosTab filtros={filtros} onJanela={registrarJanela} sidebarRef={sidebarRef} />
      )}

      {abasFuturas.length > 0 && (
        <p className="do-abas-futuras">
          Em preparação: {abasFuturas.map((aba) => aba.rotulo).join(', ')}. Depende da coleta de
          dados de eventos, ainda em construção — por isso não aparece como aba vazia.
        </p>
      )}

      {filtrosAbertos && (
        <OficinaFiltroModal
          filtrosAplicados={filtros}
          esteiras={esteiras}
          onAplicar={(novos) => {
            setFiltros(novos);
            setFiltrosAbertos(false);
          }}
          onFechar={() => setFiltrosAbertos(false)}
          sidebarRef={sidebarRef}
        />
      )}

      {assistenteAberto && (
        <AssistenteChat
          filtros={filtros}
          onFechar={() => setAssistenteAberto(false)}
          sidebarRef={sidebarRef}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Dashboard Oficina"
        secoes={helpSections}
      />
    </div>
  );
};

export default DashboardOficinaPage;
